import { Injectable } from '@angular/core';
import { isNullOrUndefined, isNumber } from 'util';
import { Observable } from 'rxjs';
import { StockChartApplicationAPIConfig } from './stock-chart/stock-chart-model';
import { StockChartUnitConversionService } from './stock-chart-shared/stock-chart-shared-services/stock-chart-unit-conversion.service';
import { LocalStorageService } from 'angular-web-storage';

@Injectable({
  providedIn: 'root'
})
export class StockChartCustomizableService {

  constructor(
    private unitConversionService: StockChartUnitConversionService,
    private localStorage: LocalStorageService
  ) { }

  titleCase(str) {
    return str.split(' ')
      .map(w => w[0].toUpperCase() + w.substr(1).toLowerCase())
      .join(' ');
  }

  getTemperatureData() {
    const temperatureData: { [key: string]: TempValue } = {
      1589653800000: {
        tMax: 42.1,
        tMin: 20.23,
        tOpen: 33.33,
        tClose: 22.1,
        tActualMax: 40.2,
        tAcutalMin: 20.22
      },
      1587666600000: {
        tMax: 42.1,
        tMin: 23.2,
        tOpen: 33.1,
        tClose: 25.7,
        tActualMax: 44.3,
        tAcutalMin: 25.2
      },
      1588530600000: {
        tMax: 44,
        tMin: 24,
        tOpen: 36,
        tClose: 26.4,
        tActualMax: 40.2,
        tAcutalMin: 26.1
      },
      1589394600000: {
        tMax: 44.5,
        tMin: 22,
        tOpen: 32.1,
        tClose: 22.4,
        tActualMax: 42.1,
        tAcutalMin: 22
      },
      1590258600000: {
        tMax: 44,
        tMin: 18,
        tOpen: 34,
        tClose: 22,
        tActualMax: 45,
        tAcutalMin: 21
      },
      1591122600000: {
        tMax: 45.2,
        tMin: 27,
        tOpen: 40.3,
        tClose: 30.3,
        tActualMax: 46.2,
        tAcutalMin: 30
      },
      1585938600000: {
        tMax: 42.5,
        tMin: 22.2,
        tOpen: 32.4,
        tClose: 25.6,
        tActualMax: 40.2,
        tAcutalMin: 21.5
      },
      1586802600000: {
        tMax: 42.3,
        tMin: 21.8,
        tOpen: 33.4,
        tClose: 25.6,
        tActualMax: 44.2,
        tAcutalMin: 22.3
      }
    };
    return temperatureData;
  }

  /**
   * custom formatted will get call when you enable yAxis formatter function Name is placed
   * @param value yAxis value
   * @param unit unit of the Axis
   * @param options yAxis formatter options
   */
  yAxisFormatter(value, unit, options) {
    value = this.formatNumberToCurrency(value);
    if (options.unitsBesideLabel) {
      value = value + ' ' + unit;
    }
    return value;
  }

  /**
   * pipe inherited from the table pipe
   * convert the value to indian currency
   * @param value value to get converted
   * @param args args
   */
  formatNumberToCurrency(value, args?: any) {
    if (isNullOrUndefined(value)) {
      return '-';
    }
    if (value >= 10000000) {
      return parseFloat((value / 10000000).toFixed(2)) + 'Cr';
    } else if (value < 10000000 && value >= 100000) {
      return parseFloat((value / 100000).toFixed(2)) + 'L';
    } else if (value < 100000 && value >= 1000) {
      return parseFloat((value / 1000).toFixed(2)) + 'K';
    } else {
      return value;
    }
  }
  /**
   * pipe inherited from the table pipe
   * convert the value to indian local number
   * @param value value to get formatted
   */
  formatNumber(value) {
    if (isNullOrUndefined(value)) {
      return '-';
    }
    if (isNumber(value)) {
      return parseFloat(value.toFixed(2)).toLocaleString('en-IN');
    } else {
      return value;
    }
  }



  /**
   * will invoke from the stock chart service config and converts to units
   * @param data stock chart formatted data
   * @param paramObject extra param object will contain fromUnit, toUnit
   */
  formatUnits(data: [number, number][], paramObject: any): Observable<any> {

    return new Observable(observer => {
      const fromUnit = paramObject.fromUnit;
      const toUnit = paramObject.toUnit;
      if (fromUnit === toUnit) {
        observer.next(data);
        observer.complete();
      }
      if (!this.isConversionPossible(paramObject.fromUnit, paramObject.toUnit)) {
        observer.next(data);
        observer.complete();
      }
      const updatedData: any[] = JSON.parse(JSON.stringify(data));
      updatedData.forEach(value => {
        value.y = this.unitConversionService.unitConversion(value.y, { from: fromUnit, to: toUnit });
      });
      observer.next(updatedData);
      observer.complete();
    });
  }
  /**
   * will verify for the conversion possible
   * you can extend this function for future project purpose currently support for MCM, MAF, TMC, BMC
   * @param fromUnit current unit
   * @param toUnit convert unit
   */
  isConversionPossible(fromUnit, toUnit) {
    if (fromUnit === 'MCM') {
      if (toUnit == 'MCM' || toUnit === 'MAF' || toUnit === 'BCM' || toUnit === 'TMC') {
        return true;
      } else {
        return false;
      }
    }
  }
  /**
   * formatter for the postData will call before an api call
   * and generate the postdata
   * @param data post Data
   */
  postDataFormatter(data, apiConfig?: StockChartApplicationAPIConfig) {
    return data;
  }

  kwrisFormatter(data) {
    let sDate = data.sDate;
    let eDate = data.eDate;
    const selectedTimeStep = data.format;
    if (selectedTimeStep == 'yyyy') {
      sDate = sDate.substring(0, 4);
      eDate = eDate.substring(0, 4);
    } else if (selectedTimeStep == 'yyyyMM') {
      sDate = sDate.substring(0, 6);
      eDate = eDate.substring(0, 6);
    } else if (selectedTimeStep == 'yyyyMMdd') {
      sDate = sDate;
      eDate = eDate;
    }
    data.sDate = sDate;
    data.eDate = eDate;
    return data;
  }

  riverpointFormatter(data) {
    const riverData = this.localStorage.get('selectedRiver');
    if (riverData && riverData['data'] && riverData['data'].length > 0) {
      const selectedRiver = riverData['data'];
      const riverDataList = [];
      for (const key in selectedRiver) {
        riverDataList.push("" + selectedRiver[key]['item_id'] + "");
      }
      data.rList = riverDataList;
    } else {
      data.rList = [];
      return data;
    }
  }

  dataFormatterForActual(data) {
    for (const key in data) {
      if (data[key] && data[key]['actual'] && data[key]['actual'] >= data[key]['normal']) {
        data[key]['greaterNormal'] = data[key]['actual'];
        // data[key]['lessNormal'] = null;
      } else if (data[key] && data[key]['normal'] && data[key]['actual'] < data[key]['normal']) {
        data[key]['lessNormal'] = data[key]['actual'];
        // data[key]['greaterNormal'] = null;
      }
    }
    return data;
  }

  groundwaterFormatter(data) {
    const sDate = data.sDate;
    const eDate = data.sDate;
    data.sDate = sDate;
    data.eDate = eDate;

    return data;
  }

  formatterForTemperatureChart(data: any, paramObject?: any) {
    const dataa: object = {};
    Object.keys(data).forEach((value, index) => {
      const obj: object = {};
      if (!isNullOrUndefined(data[value]['NORMAL'])) {
        if (!isNullOrUndefined(data[value]['NORMAL']['mn'])) {
          obj['NMIN'] = Math.round(((data[value]['NORMAL']['mn']) + Number.EPSILON) * 100) / 100;
        } else {
          obj['NMIN'] = 0;
        }
        if (!isNullOrUndefined(data[value]['NORMAL']['ag'])) {
          obj['NAVG'] = Math.round((data[value]['NORMAL']['ag'] + Number.EPSILON) * 100) / 100;
        } else {
          obj['NAVG'] = 0;
        }
        if (!isNullOrUndefined(data[value]['NORMAL']['mx'])) {
          obj['NMAX'] = Math.round((data[value]['NORMAL']['mx'] + Number.EPSILON) * 100) / 100;
        } else {
          obj['NMAX'] = 0;
        }
      } else {
        obj['NMIN'] = 0;
        obj['NAVG'] = 0;
        obj['NMAX'] = 0;
      }
      if (!isNullOrUndefined(data[value]['ACTUAL'])) {
        let minPresent = false;
        let maxPresent = false;
        if (!isNullOrUndefined(data[value]['ACTUAL']['mn'])) {
          obj['MIN'] = Math.round((data[value]['ACTUAL']['mn'] + Number.EPSILON) * 100) / 100;
          minPresent = true;
        } else {
          obj['MIN'] = 0;
        }
        if (!isNullOrUndefined(data[value]['ACTUAL']['mx'])) {
          obj['MAX'] = Math.round((data[value]['ACTUAL']['mx'] + Number.EPSILON) * 100) / 100;
          maxPresent = true;
        } else {
          obj['MAX'] = 0;
        }
        let avg: any;
        if (!minPresent && maxPresent) {
          avg = (0 + data[value]['ACTUAL']['mx']) / 2;
        } else if (minPresent && !maxPresent) {
          avg = (data[value]['ACTUAL']['mn'] + 0) / 2;
        } else if (minPresent && maxPresent) {
          avg = (data[value]['ACTUAL']['mn'] + data[value]['ACTUAL']['mx']) / 2;
        } else {
          avg = 0;
        }
        obj['AVG'] = Math.round((avg + Number.EPSILON) * 100) / 100;
      } else {
        obj['MIN'] = 0;
        obj['MAX'] = 0;
        obj['AVG'] = 0;
      }
      dataa[value] = obj;
      if (index === Object.keys(data).length - 1) {
        return dataa;
      }
    });
    return dataa;
  }

  formatterForHumidityChart(data: any, paramObject?: any) {
    const dataa: object = {};
    Object.keys(data).forEach((value, index) => {
      const obj: object = {};
      if (!isNullOrUndefined(data[value]['NORMAL'])) {
        if (!isNullOrUndefined(data[value]['NORMAL']['hn'])) {
          obj['NMIN'] = Math.round(((data[value]['NORMAL']['hn']) + Number.EPSILON) * 100) / 100;
        } else {
          obj['NMIN'] = 0;
        }
        if (!isNullOrUndefined(data[value]['NORMAL']['ag'])) {
          obj['NAVG'] = Math.round((data[value]['NORMAL']['ag'] + Number.EPSILON) * 100) / 100;
        } else {
          obj['NAVG'] = 0;
        }
        if (!isNullOrUndefined(data[value]['NORMAL']['hx'])) {
          obj['NMAX'] = Math.round((data[value]['NORMAL']['hx'] + Number.EPSILON) * 100) / 100;
        } else {
          obj['NMAX'] = 0;
        }
      } else {
        obj['NMIN'] = 0;
        obj['NAVG'] = 0;
        obj['NMAX'] = 0;
      }
      if (!isNullOrUndefined(data[value]['ACTUAL'])) {
        let minPresent = false;
        let maxPresent = false;
        if (!isNullOrUndefined(data[value]['ACTUAL']['hn'])) {
          obj['MIN'] = Math.round((data[value]['ACTUAL']['hn'] + Number.EPSILON) * 100) / 100;
          minPresent = true;
        } else {
          obj['MIN'] = 0;
        }
        if (!isNullOrUndefined(data[value]['ACTUAL']['hx'])) {
          obj['MAX'] = Math.round((data[value]['ACTUAL']['hx'] + Number.EPSILON) * 100) / 100;
          maxPresent = true;
        } else {
          obj['MAX'] = 0;
        }
        let avg: any;
        if (!minPresent && maxPresent) {
          avg = (0 + data[value]['ACTUAL']['hx']) / 2;
        } else if (minPresent && !maxPresent) {
          avg = (data[value]['ACTUAL']['hn'] + 0) / 2;
        } else if (minPresent && maxPresent) {
          avg = (data[value]['ACTUAL']['hn'] + data[value]['ACTUAL']['hx']) / 2;
        } else {
          avg = 0;
        }
        obj['AVG'] = Math.round((avg + Number.EPSILON) * 100) / 100;
      } else {
        obj['MIN'] = 0;
        obj['MAX'] = 0;
        obj['AVG'] = 0;
      }
      dataa[value] = obj;
      if (index === Object.keys(data).length - 1) {
        return dataa;
      }
    });
    return dataa;
  }
}

export interface TempValue {
  tMax: number;
  tMin: number;
  tOpen: number;
  tClose: number;
  tAcutalMin: number;
  tActualMax: number;
}
